/**
 * Meeting templates — Phase 1 of the meetings overhaul.
 *
 * A Template defines the shape of a meeting's agenda: an ordered list of
 * typed slots (opening prayer, song, business, etc.) plus some metadata
 * defaults. Templates live per-org under
 *   organizations/{orgId}/data/meetingTemplates
 * with the value envelope { templates: Template[] }.
 *
 * A Schedule entry (in stakeMeetingSchedules / customTypes) references a
 * template by id. When a virtual meeting materializes, the template's slots
 * are snapshotted into the meeting's agenda.slots[] and the meeting is
 * marked detached — subsequent template edits won't flow into it.
 *
 * A "no agenda" template (hasAgenda: false) lets the calendar host items
 * like "Mother's Day" with no agenda card rendered — just title/date/time.
 */

// ─── Slot kinds ────────────────────────────────────────────────────────────

export type SlotKind =
  | 'conducting'
  | 'opening_prayer'
  | 'closing_prayer'
  | 'song'
  | 'spiritual_thought'
  | 'speaker'
  | 'business'
  | 'prayer_list'
  | 'action_items_followup'
  | 'custom'

/** Stable list for UI menus — order = the order shown in the "+ Add element" picker. */
export const SLOT_KINDS: SlotKind[] = [
  'conducting',
  'opening_prayer',
  'song',
  'spiritual_thought',
  'speaker',
  'business',
  'action_items_followup',
  'prayer_list',
  'custom',
  'closing_prayer',
]

/** Default human-readable label for each slot kind, used when the template
 *  itself doesn't override `label`. */
export const SLOT_KIND_LABELS: Record<SlotKind, string> = {
  conducting: 'Conducting',
  opening_prayer: 'Opening Prayer',
  closing_prayer: 'Closing Prayer',
  song: 'Hymn',
  spiritual_thought: 'Spiritual Thought',
  speaker: 'Speaker',
  business: 'Business',
  prayer_list: 'Prayer List',
  // Carries open action items from previous meetings of the same type forward
  // until they're checked off. Computed at render time from
  // stakeData.actionItems — the slot itself stores nothing.
  action_items_followup: 'Action Items',
  custom: 'Custom',
}

// ─── Prayer lists ──────────────────────────────────────────────────────────

/** Identifies which of the two stake-level prayer lists a `prayer_list`
 *  slot points at. The actual names are stored in a single org-scoped doc
 *  (see PrayerListsBlob) so editing the list updates every meeting that
 *  references it. */
export type PrayerListKey = 'presidency' | 'council'

export const PRAYER_LIST_LABELS: Record<PrayerListKey, string> = {
  presidency: 'Stake Presidency',
  council: 'Stake Council',
}

/** A person on one or both stake-level prayer lists.
 *
 *  Identity is `id`, not name — renaming preserves history (when they
 *  were added, which lists they're on). Each person carries independent
 *  presidency/council bits because ~80% of names appear on both lists
 *  and we don't want to maintain two parallel rosters. */
export interface PrayerListPerson {
  id: string
  name: string
  lists: { presidency: boolean; council: boolean }
  /** ISO date the person was added to the roster. Null for legacy names
   *  migrated from the free-text blob — they predate timestamping and we
   *  don't backdate to a fictional value. */
  addedAt: string | null
}

/** Shape stored at organizations/{orgId}/data/prayerLists value.
 *
 *  `people` is the source of truth. `presidency` and `council` strings
 *  are kept as a derived denormalization (joined-by-newline) so any
 *  reader that hasn't been updated to the new shape still gets the
 *  right names — a safety net during the migration window. */
export interface PrayerListsBlob {
  people: PrayerListPerson[]
  presidency: string
  council: string
}

export const EMPTY_PRAYER_LISTS: PrayerListsBlob = {
  people: [],
  presidency: '',
  council: '',
}

/**
 * Default prayer-list key for a given meeting-type code. High Council
 * shares the Stake Council list per product spec; any unrecognized
 * type also falls back to Council (the safer default given the
 * migration source set is mostly Council-shaped).
 *
 * `type` is the canonical meeting-type code: 'presidency', 'council',
 * 'highcouncil', or any custom-type id.
 */
export function defaultPrayerListKeyForMeetingType(
  type: string | undefined,
): PrayerListKey {
  if (type === 'presidency') return 'presidency'
  return 'council'
}

/**
 * Resolve the effective `prayerListKey` for a prayer_list slot. Honors
 * a key set on the slot itself; falls back to the meeting-type default
 * for legacy slots that haven't been migrated yet. Pure — no I/O.
 */
export function resolveSlotPrayerListKey(
  slot: { prayerListKey?: PrayerListKey },
  meetingTypeCode: string | undefined,
): PrayerListKey {
  if (slot.prayerListKey === 'presidency' || slot.prayerListKey === 'council') {
    return slot.prayerListKey
  }
  return defaultPrayerListKeyForMeetingType(meetingTypeCode)
}

// ─── Prayer list helpers ───────────────────────────────────────────────────

// Split free-text name input (newlines OR commas) into trimmed names.
// Shared by the legacy-blob migration and any text-import path.
function splitNames(text: string): string[] {
  if (!text) return []
  return text
    .split(/[\n,]/)
    .map((s) => s.trim())
    .filter(Boolean)
}

function makePersonId(): string {
  return `prn-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`
}

function normalizePerson(raw: any): PrayerListPerson | null {
  if (!raw || typeof raw !== 'object') return null
  const name = typeof raw.name === 'string' ? raw.name.trim() : ''
  if (!name) return null
  const lists = raw.lists && typeof raw.lists === 'object' ? raw.lists : {}
  return {
    id: typeof raw.id === 'string' && raw.id ? raw.id : makePersonId(),
    name,
    lists: {
      presidency: lists.presidency === true,
      council: lists.council === true,
    },
    addedAt: typeof raw.addedAt === 'string' && raw.addedAt ? raw.addedAt : null,
  }
}

// Migrate the legacy {presidency: string, council: string} shape into a
// `people` array. Names appearing in both strings collapse to one
// person flagged on both lists. addedAt stays null — these names
// predate timestamping.
function peopleFromLegacyStrings(
  presidencyText: string,
  councilText: string,
): PrayerListPerson[] {
  const byName = new Map<string, PrayerListPerson>()
  const addFrom = (text: string, key: PrayerListKey) => {
    for (const name of splitNames(text)) {
      const k = name.toLowerCase()
      const existing = byName.get(k)
      if (existing) {
        existing.lists[key] = true
      } else {
        byName.set(k, {
          id: makePersonId(),
          name,
          lists: { presidency: key === 'presidency', council: key === 'council' },
          addedAt: null,
        })
      }
    }
  }
  addFrom(presidencyText, 'presidency')
  addFrom(councilText, 'council')
  return [...byName.values()]
}

/**
 * Defensive normalizer: coerce a possibly-partial Firestore value into
 * a complete PrayerListsBlob. Used by every consumer that reads the
 * raw blob from disk.
 *
 * If `people` is absent but legacy `presidency`/`council` strings are
 * present, migrates them in-memory — the strings become the seed roster
 * with both lists checked iff the name appeared in both strings. The
 * next write will persist the migrated `people` array.
 */
export function normalizePrayerListsBlob(raw: any): PrayerListsBlob {
  if (!raw || typeof raw !== 'object') return { people: [], presidency: '', council: '' }

  const presidencyText = typeof raw.presidency === 'string' ? raw.presidency : ''
  const councilText = typeof raw.council === 'string' ? raw.council : ''

  let people: PrayerListPerson[]
  if (Array.isArray(raw.people)) {
    people = raw.people
      .map(normalizePerson)
      .filter((p: PrayerListPerson | null): p is PrayerListPerson => p !== null)
  } else {
    people = peopleFromLegacyStrings(presidencyText, councilText)
  }

  // Always recompute the derived strings from the canonical people array
  // so the on-disk denormalization can't drift.
  return {
    people,
    presidency: people
      .filter((p) => p.lists.presidency)
      .map((p) => p.name)
      .join('\n'),
    council: people
      .filter((p) => p.lists.council)
      .map((p) => p.name)
      .join('\n'),
  }
}

/** Names on a given list, in roster order. */
export function getPrayerListNames(
  blob: PrayerListsBlob,
  key: PrayerListKey,
): string[] {
  return blob.people.filter((p) => p.lists[key]).map((p) => p.name)
}

/** Newline-joined names on a given list — the legacy text form. Used
 *  by consumers that still want a single string (print views, etc.). */
export function getPrayerListText(
  blob: PrayerListsBlob,
  key: PrayerListKey,
): string {
  return getPrayerListNames(blob, key).join('\n')
}

/** Add a person to the roster, defaulting them onto both lists since
 *  that's the common case (~80% overlap). Returns a new blob; never
 *  mutates. Duplicate names (case-insensitive) are rejected silently. */
export function addPrayerListPerson(
  blob: PrayerListsBlob,
  name: string,
  opts?: { lists?: Partial<Record<PrayerListKey, boolean>>; addedAt?: string },
): PrayerListsBlob {
  const trimmed = name.trim()
  if (!trimmed) return blob
  const exists = blob.people.some((p) => p.name.toLowerCase() === trimmed.toLowerCase())
  if (exists) return blob
  const person: PrayerListPerson = {
    id: makePersonId(),
    name: trimmed,
    lists: {
      presidency: opts?.lists?.presidency ?? true,
      council: opts?.lists?.council ?? true,
    },
    addedAt: opts?.addedAt ?? new Date().toISOString().slice(0, 10),
  }
  return normalizePrayerListsBlob({ ...blob, people: [...blob.people, person] })
}

export function removePrayerListPerson(
  blob: PrayerListsBlob,
  id: string,
): PrayerListsBlob {
  return normalizePrayerListsBlob({
    ...blob,
    people: blob.people.filter((p) => p.id !== id),
  })
}

export function renamePrayerListPerson(
  blob: PrayerListsBlob,
  id: string,
  name: string,
): PrayerListsBlob {
  const trimmed = name.trim()
  if (!trimmed) return blob
  return normalizePrayerListsBlob({
    ...blob,
    people: blob.people.map((p) => (p.id === id ? { ...p, name: trimmed } : p)),
  })
}

export function togglePrayerListMembership(
  blob: PrayerListsBlob,
  id: string,
  key: PrayerListKey,
  value: boolean,
): PrayerListsBlob {
  return normalizePrayerListsBlob({
    ...blob,
    people: blob.people.map((p) =>
      p.id === id ? { ...p, lists: { ...p.lists, [key]: value } } : p,
    ),
  })
}

/**
 * Reconcile a free-text list of names against the current roster for
 * one list. Used when a legacy textarea editor saves: names already on
 * the roster keep their id and addedAt and just get their `lists[key]`
 * set to true; names absent from the textarea get `lists[key]` set
 * false; new names are appended with today's date.
 *
 * The OTHER list is untouched — this only changes membership on the
 * list named by `key`. Toggling someone off both lists doesn't remove
 * them from the roster; that's an explicit action.
 */
export function applyPrayerListText(
  blob: PrayerListsBlob,
  key: PrayerListKey,
  text: string,
): PrayerListsBlob {
  const names = splitNames(text)
  const namesLower = new Set(names.map((n) => n.toLowerCase()))
  const today = new Date().toISOString().slice(0, 10)

  // Map existing people by lowercased name so we can match against the
  // textarea content without re-creating ids.
  const byName = new Map(blob.people.map((p) => [p.name.toLowerCase(), p] as const))

  const updated: PrayerListPerson[] = blob.people.map((p) => ({
    ...p,
    lists: { ...p.lists, [key]: namesLower.has(p.name.toLowerCase()) },
  }))

  // Append names that weren't already on the roster, preserving the
  // order they appeared in the textarea.
  for (const name of names) {
    if (!byName.has(name.toLowerCase())) {
      updated.push({
        id: makePersonId(),
        name,
        lists: {
          presidency: key === 'presidency',
          council: key === 'council',
        },
        addedAt: today,
      })
    }
  }

  return normalizePrayerListsBlob({ ...blob, people: updated })
}

// ─── Template-level slot definition ────────────────────────────────────────

/** Pre-fill defaults that flow into a slot when materialized. All optional;
 *  which subset is meaningful depends on the slot kind:
 *    conducting                      → assignee
 *    opening_prayer / closing_prayer → assignee
 *    song                            → hymnNumber, hymnTitle
 *    spiritual_thought / speaker     → assignee, topic, durationMin
 *    business                        → (none — items are per-meeting)
 *    custom                          → notes, durationMin
 */
export interface SlotDefaults {
  assignee?: string
  notes?: string
  hymnNumber?: string
  hymnTitle?: string
  topic?: string
  durationMin?: number
}

/** A slot in a template — definition only, no per-meeting values. */
export interface TemplateSlot {
  id: string
  kind: SlotKind
  /** Override for the default kind label, e.g. "Opening Hymn" instead of "Hymn". */
  label?: string
  defaults?: SlotDefaults
  /** Required when kind === 'prayer_list': which of the two stake-level
   *  prayer lists this slot renders. The names themselves live in the
   *  PrayerListsBlob, not on the slot. */
  prayerListKey?: PrayerListKey
  /** Only meaningful when kind === 'business'. Topics that always appear
   *  in this meeting's Business section (e.g. "Review callings",
   *  "Upcoming calendar events"). NOT materialized onto the meeting — they
   *  merge LIVE from the template at render time (the renderer reads this
   *  array and records only thin {id, kind:'recurring'} tombstones in
   *  agenda.items[] once the user drags). Legacy data may still contain a
   *  reserved "Non-Recurring Items" placeholder (see NON_RECURRING_MARKER_ID);
   *  it is retired — filtered out everywhere and never rendered as a topic. */
  recurringItems?: AgendaItem[]
  /** Explicit "this slot starts (or sits in) the Closing section" marker.
   *
   *  Section membership is normally position-derived from the business
   *  slot(s) — see partitionTemplateSlots. Templates with no business slot
   *  have no positional boundary, so an element added via "Add to Closing"
   *  would otherwise render under Opening. This flag supplies the missing
   *  boundary. Only ever set to 'closing'; absent means "let position
   *  decide". Ignored whenever the template has a business slot. */
  section?: 'closing'
}

// ─── Section partitioning ──────────────────────────────────────────────────

/**
 * Index at which the Closing section begins.
 *
 * Preference order:
 *   1. Just after the LAST business slot, when the template has one. The
 *      business slot(s) are the real Opening/Closing divider.
 *   2. Otherwise the first "closing anchor" — a closing_prayer slot, or any
 *      slot explicitly flagged `section: 'closing'` (set by the template
 *      editor's "Add to Closing" menu when there is no business slot).
 *   3. Otherwise slots.length — Closing is empty.
 *
 * Returns slots.length for an empty list.
 */
export function closingStartIndex(slots: readonly TemplateSlot[] | undefined): number {
  if (!slots || slots.length === 0) return 0
  for (let i = slots.length - 1; i >= 0; i--) {
    if (slots[i].kind === 'business') return i + 1
  }
  const anchor = slots.findIndex(
    (s) => s.kind === 'closing_prayer' || s.section === 'closing',
  )
  return anchor === -1 ? slots.length : anchor
}

/**
 * Split a template's slots into Opening / Business / Closing.
 *
 * Business is the inclusive span between the first and last business slot;
 * Opening is everything before it; Closing is everything from
 * closingStartIndex() on. Shared by the template editor and both meeting
 * renderers so all three agree on which section a slot lives in.
 */
export function partitionTemplateSlots(slots: readonly TemplateSlot[] | undefined): {
  opening: TemplateSlot[]
  business: TemplateSlot[]
  closing: TemplateSlot[]
} {
  if (!slots || slots.length === 0) return { opening: [], business: [], closing: [] }
  const list = slots as TemplateSlot[]
  const firstBiz = list.findIndex((s) => s.kind === 'business')
  const closingStart = closingStartIndex(list)
  if (firstBiz === -1) {
    return {
      opening: list.slice(0, closingStart),
      business: [],
      closing: list.slice(closingStart),
    }
  }
  return {
    opening: list.slice(0, firstBiz),
    business: list.slice(firstBiz, closingStart),
    closing: list.slice(closingStart),
  }
}

// ─── Template ──────────────────────────────────────────────────────────────

/** Identifiers for the templates we ship as built-in starters. Cloned into
 *  each org on first read of the meetingTemplates blob. The set seeded
 *  varies by org type (see cloneSystemTemplates(orgType) in
 *  apps/web/src/stake/lib/templates.ts). */
export type SystemTemplateSeed =
  | 'bishopric'
  | 'ward_council'
  | 'pec'
  | 'stake_presidency'
  | 'stake_council'
  | 'high_council'
  | 'no_agenda'

export interface TemplateDefaults {
  conducting?: string
  location?: string
}

export interface Template {
  id: string
  name: string
  /** Bumped on every save; used by meetings to detect upgrade availability. */
  version: number
  /** Set when this template was cloned from a system seed; null/undefined for
   *  hand-built templates. Lets us know whether to show "Reset to default". */
  systemSeed?: SystemTemplateSeed | null
  /** When false, this template renders no agenda card on the meeting view —
   *  only the calendar metadata. Slots is forced empty. Used for entries
   *  like "Mother's Day" or stake-conference visits. */
  hasAgenda: boolean
  defaults?: TemplateDefaults
  slots: TemplateSlot[]
}

// ─── Meeting-instance agenda (runtime form) ────────────────────────────────

/** A business-slot item. Items live on their owning meeting; carry-forward
 *  uses sourceMeetingId so completion stays single-source-of-truth. */
export interface AgendaItem {
  id: string
  text: string
  requestor?: string
  completed?: boolean
  completedAt?: string
  completedBy?: string
  /** When the user marks this complete, look up the source meeting and
   *  mark complete there instead of duplicating. Set only on items that
   *  were carried forward from a prior meeting. */
  sourceMeetingId?: string
  sourceMeetingDate?: string
  /** 'recurring' items come from the template's business slot recurringItems[]
   *  and can't be completed — they're informational topics that always appear
   *  (e.g. "Review callings"). 'todo' items are per-meeting and checkable.
   *  Absent / undefined is treated as 'todo' for back-compat with existing data. */
  kind?: 'recurring' | 'todo'
  /** Person who owns this recurring topic, shown after the title with a
   *  separator dot. Only meaningful on kind === 'recurring' items. */
  assignee?: string
  /** Free-form background / talking points for a per-meeting business item,
   *  edited in the business-item sheet. Optional; absent on most items. */
  notes?: string
  /** When set, this recurring business item renders an interactive affordance
   *  instead of plain text (in-meeting). Only meaningful on kind === 'recurring':
   *   - 'callings'    → count pill that opens the Callings review surface
   *                     (drawer on web, pushed screen on iOS). The list is
   *                     auto-scoped by meeting type (High Council → HC-visible
   *                     callings; otherwise the full in-progress stake list).
   *   - 'ordinations' → count pill that opens the Ordinations review surface.
   *                     A single in-progress list, the same for every meeting
   *                     type (no presidency-vs-HC scoping).
   *   - 'url'         → an external-link affordance that opens `linkUrl`. */
  link?: 'callings' | 'ordinations' | 'url'
  /** Target for a link === 'url' item (an external web address). Ignored for
   *  the 'callings' / 'ordinations' link kinds. */
  linkUrl?: string
  /** Retired. Reserved marker that used to flag the "Non-Recurring Items"
   *  placeholder inside a template business slot's recurringItems[]. The
   *  placeholder feature is gone — carried-forward items now arrive via an
   *  explicit "→ Next" push and render inline as ordinary business items. Kept
   *  only so isNonRecurringMarker can filter any marker still present in legacy
   *  template data out of display. Never written anymore. */
  placeholder?: boolean
}

/** Reserved id for the retired "Non-Recurring Items" placeholder that may still
 *  live in legacy template data's recurringItems[]. Fixed (not a random uid) so
 *  every layer can detect and filter it cheaply. */
export const NON_RECURRING_MARKER_ID = '__nonrecurring__'

/** True when an agenda item is the retired "Non-Recurring Items" placeholder.
 *  Used to filter any leftover marker out of the rendered recurring-topic list
 *  and out of the template editor so it never surfaces as a real topic. */
export function isNonRecurringMarker(
  item: { id?: string; placeholder?: boolean } | null | undefined,
): boolean {
  return !!item && (item.placeholder === true || item.id === NON_RECURRING_MARKER_ID)
}

/** Build a "Non-Recurring Items" placeholder marker. Retired — used only by
 *  tests to simulate legacy template data that the filters must drop. */
export function makeNonRecurringMarker(): AgendaItem {
  return {
    id: NON_RECURRING_MARKER_ID,
    text: 'Non-Recurring Items',
    kind: 'recurring',
    placeholder: true,
  }
}

/** A slot inside a meeting's agenda. Snapshotted from a TemplateSlot at
 *  materialization time, then mutable. `kind` tells you which optional
 *  fields are meaningful. */
export interface AgendaSlot {
  id: string
  kind: SlotKind
  label?: string
  assignee?: string
  notes?: string
  completed?: boolean
  // Slot-kind-specific (all optional):
  hymnNumber?: string
  hymnTitle?: string
  topic?: string
  durationMin?: number
  // For kind === 'business':
  items?: AgendaItem[]
  /** For kind === 'prayer_list': which of the two stake-level prayer lists
   *  this slot renders. The names themselves live in the PrayerListsBlob
   *  (org-scoped), so edits propagate to every meeting that references the
   *  same key. Legacy slots written before the global-prayer-lists
   *  refactor have this field absent and a per-slot `notes` string instead;
   *  see migrateAgendaSlotPrayerKey() in the prayerLists helper. */
  prayerListKey?: PrayerListKey
}

/** The agenda blob on a meeting instance. Other meeting fields (date, time,
 *  location, conducting, scheduleKey, …) live on the meeting itself. */
export interface MeetingAgenda {
  slots: AgendaSlot[]
  /** ISO timestamp stamped on every agenda edit. Surfaces "agenda last changed
   *  May 16" on the meetings overview so the user can see staleness at a glance.
   *  Absent on agendas written before the timestamp was introduced. */
  agendaUpdatedAt?: string
  /** Legacy fields, kept on disk during the migration window. After the
   *  cleanup PR drops them, this comment can go too. */
  openingPrayer?: string
  openingSongNumber?: string
  spiritualThought?: string
  closingPrayer?: string
  conducting?: string
  items?: AgendaItem[]
}

// ─── Persistence envelopes ─────────────────────────────────────────────────

/** Shape stored at organizations/{orgId}/data/meetingTemplates value. */
export interface MeetingTemplatesBlob {
  templates: Template[]
}

/** Fields added to schedule entries (built-in or custom) when they reference
 *  a template. The legacy schedule fields (freq, day, time, week, location)
 *  are unchanged. */
export interface ScheduleTemplateLink {
  templateId?: string
}

/** Fields added to a meeting instance when it materializes against a template. */
export interface MeetingTemplateSnapshot {
  templateId?: string
  templateVersion?: number
  /** True once the user has edited the agenda; further template updates
   *  won't flow into this meeting. */
  detached?: boolean
}

// ─── Helpers ───────────────────────────────────────────────────────────────

/** Resolve the display label for a slot, preferring the template-level
 *  override when present and falling back to the canonical kind label. */
export function slotLabel(slot: { kind: SlotKind; label?: string }): string {
  return slot.label || SLOT_KIND_LABELS[slot.kind]
}

/** Whether a template should render an agenda section. Convenience for
 *  guarding against templates without slots. */
export function templateHasAgenda(template: Pick<Template, 'hasAgenda'>): boolean {
  return template.hasAgenda !== false
}

/**
 * Map a meeting's canonical type code → the system-seed starter that matches
 * it. Used as the last-resort fallback in {@link resolveTemplate}. Custom
 * types have no seed and intentionally aren't listed (they map to null and
 * stay calendar-only). Keep in sync with ONE_OFF_TYPE_TO_SYSTEM_SEED in
 * apps/web/src/stake/StakeMeetingsTab.tsx (the one-off-creation default).
 */
export const MEETING_TYPE_TO_SYSTEM_SEED: Record<string, SystemTemplateSeed> = {
  presidency: 'stake_presidency',
  council: 'stake_council',
  highcouncil: 'high_council',
  bishopric: 'bishopric',
  wardcouncil: 'ward_council',
}

/**
 * Resolve which template applies to a meeting. Lookup order:
 *   1. meeting.templateId — stamped when a meeting materializes (PR #8). If it
 *      no longer resolves (templates re-cloned with fresh ids) fall THROUGH
 *      rather than giving up.
 *   2. The meeting's schedule's templateId (built-in key or customTypes entry).
 *   3. Resilience fallback: the system-seed starter matching the meeting's
 *      `type` — but ONLY when the meeting actually carried a templateId or
 *      scheduleKey (i.e. it's meant to have an agenda). A bare one-off with
 *      neither stays null so callers treat it as "no agenda". This is what
 *      keeps a templates reseed (which orphans every schedule→template link)
 *      from silently blanking every recurring meeting's agenda.
 *   4. null.
 *
 * SHARED by web + mobile. Previously hand-copied into three files that drifted
 * — the mobile copies lacked the step-1 fall-through, so a stale per-meeting
 * id blanked the agenda on iOS only. Pure; no I/O. `meeting`/`schedules` are
 * loosely typed (`any`) to match the raw Firestore blobs both surfaces pass.
 */
export function resolveTemplate(
  meeting: any,
  schedules: any,
  meetingTemplates: MeetingTemplatesBlob | null | undefined,
): Template | null {
  const templates = meetingTemplates?.templates || []
  let attempted = false
  if (meeting?.templateId) {
    attempted = true
    const direct = templates.find((t) => t.id === meeting.templateId)
    if (direct) return direct
  }
  if (meeting?.scheduleKey) {
    attempted = true
    const builtin = schedules?.[meeting.scheduleKey]
    const custom = (schedules?.customTypes || []).find(
      (c: any) => c.id === meeting.scheduleKey,
    )
    const tplId = (builtin || custom)?.templateId
    if (tplId) {
      const viaSchedule = templates.find((t) => t.id === tplId)
      if (viaSchedule) return viaSchedule
    }
  }
  if (attempted) {
    const seed = MEETING_TYPE_TO_SYSTEM_SEED[meeting?.type]
    if (seed) {
      const bySeed = templates.find((t) => t.systemSeed === seed)
      if (bySeed) return bySeed
    }
  }
  return null
}
